import { Video, Users, Clock, Calendar, User } from 'lucide-react';
import type { TopMeeting, UsageData, NotAvailable } from './overview-types';
import { isNotAvailable } from './overview-types';

interface TopMeetingsProps {
  usage: UsageData | NotAvailable;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function MeetingCard({ meeting, rank }: { meeting: TopMeeting; rank: number }) {
  return (
    <div className="flex gap-4 rounded-lg border border-border bg-card/50 p-4">
      <div className="relative h-8 w-8 shrink-0 rounded-full bg-primary/10">
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-primary leading-none">{rank}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground break-words">{meeting.topic}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          <Stat icon={<Calendar className="h-3 w-3" />} label={`${meeting.occurrences}x`} />
          <Stat icon={<Users className="h-3 w-3" />} label={`~${meeting.avgParticipants} avg`} />
          <Stat icon={<Clock className="h-3 w-3" />} label={formatDuration(meeting.avgDuration)} />
          <Stat icon={<User className="h-3 w-3" />} label={meeting.host} />
        </div>
        <p className="mt-1.5 text-[11px] text-muted-foreground/60">
          Last held {formatDate(meeting.lastOccurred)} &middot; {formatDuration(meeting.totalDuration)} total
        </p>
      </div>
    </div>
  );
}

function Stat({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      {icon}
      {label}
    </span>
  );
}

export function TopMeetings({ usage }: TopMeetingsProps) {
  if (isNotAvailable(usage)) return null;
  const data = usage as UsageData;
  if (!data.topMeetings || data.topMeetings.length === 0) return null;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Video className="h-5 w-5 text-muted-foreground" />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Top Meetings</h3>
        <span className="text-xs text-muted-foreground">by frequency</span>
      </div>
      <div className="space-y-3 p-5">
        {data.topMeetings.slice(0, 3).map((meeting, i) => (
          <MeetingCard key={meeting.topic} meeting={meeting} rank={i + 1} />
        ))}
      </div>
    </div>
  );
}
